<template>
    <div class="col-md-12">
        <div class="d-flex align-items-center justify-content-between">
            <div>
                <div class="text-muted" style="font-size: 0.85rem;">
                    <a href="/cockpit" class="text-muted">Cockpit</a> / Configuration
                </div>
                <div class="h2">Cockpit configuration</div>
                <div class="text-muted">
                    Enable or disable warnings, and adjust thresholds and agreement type for each rule.
                    Changes take effect at the next evaluation of bookings.
                </div>
            </div>
            <a href="/cockpit" class="btn btn-outline-secondary">&larr; Back to overview</a>
        </div>

        <div class="flash-message">
            <FlashMessage :position="'top'"></FlashMessage>
        </div>

        <div class="card mt-4">
            <table class="table table-striped mb-0" aria-label="Cockpit rules table">
                <thead>
                    <tr>
                        <th style="width: 7%;">Active</th>
                        <th style="width: 38%;">Rule</th>
                        <th style="width: 20%;">Threshold</th>
                        <th style="width: 13%;">Agreement</th>
                        <th style="width: 12%;">Warnings</th>
                        <th style="width: 10%;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="rule in rules" :key="rule.id" :class="{'text-muted': !rule.enabled}">
                        <td>
                            <div class="custom-control custom-switch">
                                <input type="checkbox"
                                       class="custom-control-input"
                                       :id="`rule-toggle-${rule.id}`"
                                       :checked="rule.enabled"
                                       @change="toggleRule(rule, $event)">
                                <label class="custom-control-label" :for="`rule-toggle-${rule.id}`"></label>
                            </div>
                        </td>
                        <td>
                            <div class="font-weight-bold">{{ rule.name }}</div>
                            <div class="text-muted" style="font-size: 0.85rem;">{{ rule.description }}</div>
                        </td>
                        <td>
                            <span class="badge badge-secondary mr-1">Cut-off: {{ cutOffLabel(rule) }}</span>
                            <span v-if="rule.settings.threshold_days || rule.default_settings.threshold_days"
                                  class="badge badge-secondary mr-1">
                                {{ rule.settings.threshold_days || rule.default_settings.threshold_days }} working days
                            </span>
                            <span v-if="rule.settings.lookback_days || rule.default_settings.lookback_days"
                                  class="badge badge-light">
                                Lookback: {{ rule.settings.lookback_days || rule.default_settings.lookback_days }} days
                            </span>
                            <span v-if="rule.settings.age_days || rule.default_settings.age_days"
                                  class="badge badge-secondary mr-1">
                                Older than {{ rule.settings.age_days || rule.default_settings.age_days }} days
                            </span>
                        </td>
                        <td>
                            <span v-for="type in rule.booking_types" :key="type" class="badge badge-info mr-1">
                                {{ typeName(type) }}
                            </span>
                        </td>
                        <td>
                            <span v-if="rule.open_warnings > 0" class="text-danger font-weight-bold">
                                {{ rule.open_warnings }} open
                            </span>
                            <span v-else class="text-muted">None</span>
                        </td>
                        <td>
                            <button class="btn btn-sm btn-outline-secondary" @click="openEdit(rule)">Edit</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="editingRule" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.45);">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Edit rule: {{ editingRule.name }}</h5>
                        <button type="button" class="close" @click="closeEdit">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted">{{ editingRule.description }}</p>

                        <div class="form-group">
                            <div class="custom-control custom-switch">
                                <input type="checkbox" class="custom-control-input" id="edit-enabled" v-model="form.enabled">
                                <label class="custom-control-label" for="edit-enabled">
                                    {{ form.enabled ? 'Active' : 'Disabled' }}
                                </label>
                            </div>
                            <small class="text-muted">
                                When turned off, the rule is not evaluated and generates no new warnings.
                            </small>
                        </div>

                        <div class="form-group" v-if="hasSetting('cut_off_time')">
                            <label for="edit-cut-off">Cut-off time</label>
                            <input type="text"
                                   id="edit-cut-off"
                                   class="form-control"
                                   placeholder="System cut-off"
                                   v-model="form.cut_off_time">
                            <small class="text-muted">
                                Format HH:MM. Leave empty to follow the system cut-off from the configuration page.
                            </small>
                        </div>

                        <div class="form-group" v-if="hasSetting('threshold_days')">
                            <label for="edit-threshold">Deadline (working days)</label>
                            <input type="number" min="1" max="30"
                                   id="edit-threshold"
                                   class="form-control"
                                   v-model.number="form.threshold_days">
                        </div>

                        <div class="form-group" v-if="hasSetting('lookback_days')">
                            <label for="edit-lookback">Lookback window (days)</label>
                            <input type="number" min="1" max="90"
                                   id="edit-lookback"
                                   class="form-control"
                                   v-model.number="form.lookback_days">
                            <small class="text-muted">
                                Violations older than this are not shown, to keep historic backlog out of the cockpit.
                            </small>
                        </div>

                        <div class="form-group" v-if="hasSetting('age_days')">
                            <label for="edit-age">Booking age (calendar days)</label>
                            <input type="number" min="1" max="90"
                                   id="edit-age"
                                   class="form-control"
                                   v-model.number="form.age_days">
                            <small class="text-muted">
                                Bookings created more than this many days ago without received goods give a warning.
                            </small>
                        </div>

                        <div class="form-group">
                            <label>Agreement</label>
                            <div>
                                <div class="form-check form-check-inline" v-for="type in [3, 4]" :key="type">
                                    <input type="checkbox"
                                           class="form-check-input"
                                           :id="`edit-type-${type}`"
                                           :value="type"
                                           v-model="form.booking_types">
                                    <label class="form-check-label" :for="`edit-type-${type}`">{{ typeName(type) }}</label>
                                </div>
                            </div>
                        </div>

                        <div class="text-danger" v-if="error">{{ error }}</div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary" @click="closeEdit">Cancel</button>
                        <button class="btn btn-primary" :disabled="!form.booking_types.length || saving" @click="save">
                            Save changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CockpitConfiguration",
    data() {
        return {
            rules: [],
            editingRule: null,
            form: {
                enabled: true,
                cut_off_time: '',
                threshold_days: null,
                lookback_days: null,
                age_days: null,
                booking_types: [],
            },
            saving: false,
            error: null,
        };
    },
    created() {
        this.fetchRules();
    },
    beforeDestroy() {
        document.body.classList.remove('modal-open');
    },
    methods: {
        fetchRules() {
            axios.get('/cockpit/rules').then(response => {
                this.rules = response.data.data;
            }).catch(() => {
                this.flashMessage.error({title: 'Error', message: 'Could not load rules.', time: 4000});
            });
        },
        typeName(type) {
            return type === 4 ? 'Ad-hoc' : (type === 3 ? 'Flex' : type);
        },
        cutOffLabel(rule) {
            if (!('cut_off_time' in rule.default_settings)) {
                return '—';
            }
            return rule.settings.cut_off_time || 'System';
        },
        hasSetting(key) {
            return this.editingRule && (key in this.editingRule.default_settings);
        },
        openEdit(rule) {
            document.body.classList.add('modal-open');
            this.editingRule = rule;
            this.error = null;
            this.form = {
                enabled: rule.enabled,
                cut_off_time: rule.settings.cut_off_time || '',
                threshold_days: rule.settings.threshold_days || rule.default_settings.threshold_days || null,
                lookback_days: rule.settings.lookback_days || rule.default_settings.lookback_days || null,
                age_days: rule.settings.age_days || rule.default_settings.age_days || null,
                booking_types: [...rule.booking_types],
            };
        },
        closeEdit() {
            document.body.classList.remove('modal-open');
            this.editingRule = null;
        },
        toggleRule(rule, event) {
            this.persist(rule, {
                enabled: !rule.enabled,
                booking_types: rule.booking_types,
                settings: rule.settings,
            }, null, () => {
                event.target.checked = !!rule.enabled;
            });
        },
        save() {
            const settings = {};
            if (this.hasSetting('cut_off_time')) {
                settings.cut_off_time = this.form.cut_off_time || null;
            }
            if (this.hasSetting('threshold_days') && this.form.threshold_days) {
                settings.threshold_days = this.form.threshold_days;
            }
            if (this.hasSetting('lookback_days') && this.form.lookback_days) {
                settings.lookback_days = this.form.lookback_days;
            }
            if (this.hasSetting('age_days') && this.form.age_days) {
                settings.age_days = this.form.age_days;
            }

            this.persist(this.editingRule, {
                enabled: this.form.enabled,
                booking_types: this.form.booking_types,
                settings: settings,
            }, () => this.closeEdit());
        },
        persist(rule, payload, onSuccess = null, onError = null) {
            this.saving = true;
            this.error = null;
            axios.post(`/cockpit/rules/${rule.id}`, payload).then(() => {
                this.flashMessage.success({title: 'Success', message: 'Rule saved.', time: 3000});
                if (onSuccess) {
                    onSuccess();
                }
                this.fetchRules();
            }).catch(error => {
                const errors = error.response?.data?.errors;
                this.error = errors ? Object.values(errors)[0][0] : 'Could not save the rule.';
                if (!this.editingRule) {
                    this.flashMessage.error({title: 'Error', message: this.error, time: 4000});
                }
                if (onError) {
                    onError();
                }
                this.fetchRules();
            }).finally(() => {
                this.saving = false;
            });
        },
    },
};
</script>
